/**
 * VariantPicker.tsx — THE STRENGTH CONTROL. The half of the prescribing design
 * that was missing, and whose absence caused the 2026-09-11 defect.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: prescribing truth
 * (2026-09-12).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHAT IT IS. The prescriber picks the EXACT licensed variant from their own
 * order's family. The list arrives at runtime from `GET /v1/prices/formulary`,
 * which serves each variant's licensed description WHOLE alongside the same
 * string decomposed into `drugName` / `strength` / `formulation` / `device` by a
 * human, in a reviewed diff, proven to recompose byte-for-byte.
 *
 * FOUR RULES, EACH ONE A DEFECT THAT ACTUALLY HAPPENED:
 *
 *  1. THE FULL DISPENSING NAME IS THE OPTION LABEL. That exact string is what
 *     the pharmacist reads on the document, so it is what the prescriber chooses
 *     from. A prettified label would mean the clinician and the dispenser were
 *     looking at two different descriptions of one medicine.
 *
 *  2. NOTHING IS PARSED. The strength shown beneath the select is `variant.
 *     strength` off the wire — not a substring of the name. This portal used to
 *     regex a strength out of a display string, and the pattern it used matched
 *     a PRICING ANCHOR exactly.
 *
 *  3. NO PRICE, EVER. The formulary response carries none (prices-service parses
 *     it `.strict()`), and none is shown, derived or hinted at here. The patient
 *     is priced at the family's anchor; which variant a clinician picks must
 *     never move what anyone is charged. Money on a prescribing screen is how a
 *     commercial figure became a clinical one last time.
 *
 *  4. NO DEFAULT SELECTION. The select opens on an empty placeholder and the
 *     form will not submit without a choice. A preselected variant is a strength
 *     nobody chose — which is exactly what the anchor was.
 *
 * AND THE FAILURE MODE IS A BLOCK, NOT A FALLBACK. If the read fails this
 * renders a refusal and the caller must not submit. Everywhere else in this
 * portal a failed list read degrades to free text; here that would reinstate the
 * free-text strength box this control replaced. See `useFormulary`.
 *
 * R28: not one medicine name exists in this file, or in the tests for it. Every
 * name on this screen is served at runtime.
 * ═════════════════════════════════════════════════════════════════════════════
 */
import { Alert, Button, Field, Help, Select, Spinner } from '../ui/kit';
import { invalidControlStyle } from '../ui/ErrorSummary';
import type { FormularyState } from './useFormulary';
import type { MedicationVariant } from '../api/types';

/** The copy the caller shows when the list is unavailable — one sentence, one place. */
export const FORMULARY_UNAVAILABLE =
  'The licensed strength list could not be loaded, so this prescription cannot be issued yet.';

/** The empty-choice placeholder. Exported so the specs assert the real string. */
export const CHOOSE_STRENGTH = 'Choose the exact strength…';

/**
 * The decomposed facts of the chosen variant, each read straight off the wire.
 *
 * WHY REPEAT THEM UNDER A SELECT THAT ALREADY SHOWS THE WHOLE NAME: because the
 * whole name is long, the strength is the part that matters clinically, and the
 * two are one fact in five fields — showing them side by side is how a prescriber
 * can SEE that the parts and the whole agree before they sign. It is also the
 * shape of the data being made visible: these are fields, not fragments someone
 * cut out of a label.
 */
function ChosenVariantFacts({ variant }: { variant: MedicationVariant }) {
  const rows: Array<[string, string]> = [
    ['Strength', variant.strength],
    ['Formulation', variant.formulation],
    ...(variant.device ? ([['Device', variant.device]] as Array<[string, string]>) : []),
  ];
  return (
    <div
      style={{
        border: '1px solid var(--gps-border)',
        borderRadius: 'var(--gps-r-md)',
        padding: '10px 12px',
        marginTop: '8px',
        background: 'var(--gps-surface-2)',
      }}
    >
      <p style={{ margin: '0 0 6px', fontSize: '12.5px', fontWeight: 600, color: 'var(--gps-muted)' }}>
        What the pharmacist will read
      </p>
      <p style={{ margin: '0 0 8px', fontSize: '14px', fontWeight: 600, color: 'var(--gps-text)' }}>{variant.name}</p>
      <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 10px', margin: 0, fontSize: '13px' }}>
        {rows.map(([label, value]) => (
          <div key={label} style={{ display: 'contents' }}>
            <dt style={{ color: 'var(--gps-muted)' }}>{label}</dt>
            <dd style={{ margin: 0, color: 'var(--gps-text)', fontWeight: 600 }}>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * The strength select for one prescription line.
 *
 * The caller owns the read (`useFormulary`) because a multi-line prescribe form
 * has one family per line while the correction lane has exactly one — and
 * because the caller is who has to refuse to submit when it failed.
 */
export function VariantPicker({
  id,
  label = 'Strength',
  formulary,
  value,
  onChange,
  onRetry,
  disabled = false,
  idleHint = 'Choose a medication first — its licensed strengths follow from it.',
  required = true,
  error,
}: {
  id: string;
  label?: string;
  formulary: FormularyState;
  /** The chosen `variantKey`, or '' for "not chosen yet". */
  value: string;
  /**
   * The chosen key, and the whole variant beside it for DISPLAY ONLY.
   *
   * The caller gets the variant so that a confirmation screen can show the
   * licensed description the pharmacist will read, instead of an opaque key. It
   * must never be put on the wire: the request carries the key and the SERVER
   * resolves it (R41), and the contract rejects a client-posted snapshot.
   */
  onChange: (variantKey: string, variant: MedicationVariant | null) => void;
  onRetry: () => void;
  disabled?: boolean;
  idleHint?: string;
  /**
   * The label's "*" and the select's `aria-required` (2026-09-25). Default
   * TRUE because the strength is required on every line this control has ever
   * been mounted on — see the header: the form will not submit without it.
   */
  required?: boolean;
  /**
   * The inline error the caller's error summary names for this line ("Choose a
   * strength for line 1"). Rendered under the select WITHOUT `role="alert"` —
   * the summary is the one assertive region — and tied to the select by
   * `aria-describedby`, with `aria-invalid`. Shown only in the ready state:
   * while loading or failed there is no select for it to describe, and the
   * failure alert already says what is wrong.
   */
  error?: string;
}) {
  const errorId = `${id}-error`;
  if (formulary.state === 'idle') return <Help>{idleHint}</Help>;
  if (formulary.state === 'loading') return <Spinner label="Loading the licensed strengths…" />;

  if (formulary.state === 'error') {
    return (
      <Alert tone="danger" role="alert">
        <strong style={{ display: 'block', fontWeight: 600 }}>{FORMULARY_UNAVAILABLE}</strong>
        <span style={{ display: 'block', marginTop: '4px' }}>
          Nothing has been changed. There is deliberately no free-text strength box here — a typed strength is what
          this list replaced. Try again, and if it keeps failing, tell the team rather than working around it.
        </span>
        {/* An id so the error summary's "Choose a strength" link can land HERE
            when the list failed — the one control that can bring it back. */}
        <Button
          id={`${id}-retry`}
          type="button"
          variant="ghost"
          onClick={onRetry}
          style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', marginTop: '10px' }}
        >
          Try again
        </Button>
      </Alert>
    );
  }

  // Pulled out as a const so the narrowing survives into the onChange closure.
  const { family } = formulary;
  const chosen = family.variants.find((v) => v.variantKey === value);

  return (
    <>
      <Field label={label} htmlFor={id} required={required} error={error} errorId={errorId} errorAlert={false}>
        <Select
          id={id}
          value={value}
          disabled={disabled}
          aria-required={required || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          style={invalidControlStyle(error)}
          onChange={(e) =>
            onChange(e.target.value, family.variants.find((v) => v.variantKey === e.target.value) ?? null)
          }
        >
          <option value="">{CHOOSE_STRENGTH}</option>
          {/* The FULL licensed description, in the order the formulary serves it
              (the titration ladder, lowest first). Not sorted, not shortened:
              re-ordering a dose ladder alphabetically would put 10 before 5. */}
          {family.variants.map((variant) => (
            <option key={variant.variantKey} value={variant.variantKey}>
              {variant.name}
            </option>
          ))}
        </Select>
      </Field>
      {chosen ? <ChosenVariantFacts variant={chosen} /> : null}
      {/* NO PROVENANCE LINE HERE (CTO, 2026-09-13). This used to print
          `Strengths from: {formulary.source}`, which rendered as "CTO formulary
          sheet, transcribed 2026-09-11 (decomposed fields verified lossless)" —
          our internal transcription process, on a clinical control, in front of
          a prescriber. How the list reached us is an engineering fact, not a
          prescribing one; the safeguard that matters is that the strength is
          CHOSEN from this server-served list and can never be typed, which the
          picker enforces regardless of whether its origin is captioned.
          `source` is still served by the formulary contract and still used for
          audit — it is only unprinted here. */}
    </>
  );
}
